import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import RecipeCard from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { BookOpen, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const MyRecipesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [recipes, setRecipes] = useState([]);
    const [favorites, setFavorites] = useState(new Set());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        loadMyRecipes();
    }, [user, navigate]);

    const loadMyRecipes = async () => {
        setLoading(true);
        try {
            const [recipesRes, favsRes] = await Promise.all([
                api.getMyRecipes(),
                api.getFavorites()
            ]);
            setRecipes(recipesRes.data);
            setFavorites(new Set(favsRes.data.map(r => r.id)));
        } catch (error) {
            console.error('Error loading recipes:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleFavoriteToggle = async (recipeId) => {
        try {
            if (favorites.has(recipeId)) {
                await api.removeFavorite(recipeId);
                setFavorites(prev => {
                    const next = new Set(prev);
                    next.delete(recipeId);
                    return next;
                });
                toast.success('Retiré des favoris');
            } else {
                await api.addFavorite(recipeId);
                setFavorites(prev => new Set([...prev, recipeId]));
                toast.success('Ajouté aux favoris');
            }
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    return (
        <div className="min-h-screen py-8 md:py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                                Mes recettes
                            </h1>
                            <p className="text-muted-foreground">
                                Les recettes que vous avez créées
                            </p>
                        </div>
                    </div>
                    
                    <Button
                        onClick={() => navigate('/recipes/new')}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                        data-testid="create-recipe-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle recette
                    </Button>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : recipes.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                            <BookOpen className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                            Aucune recette créée
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            Partagez vos meilleures recettes économiques avec la communauté!
                        </p>
                        <Button
                            onClick={() => navigate('/recipes/new')}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Créer ma première recette
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="text-muted-foreground mb-6">
                            {recipes.length} recette{recipes.length > 1 ? 's' : ''} créée{recipes.length > 1 ? 's' : ''}
                        </p>
                        
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {recipes.map((recipe, index) => (
                                <div key={recipe.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                                    <RecipeCard
                                        recipe={recipe}
                                        isFavorite={favorites.has(recipe.id)}
                                        onFavoriteToggle={handleFavoriteToggle}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyRecipesPage;
